<user-management>
  <div class="container mt-4">
    <page-header
      title="Gestión de Usuarios"
      subtitle="Visualiza y administra las cuentas de usuario del sistema"
      onback={ handleBack }>
      <button slot="actions" class="btn btn-primary app-btn app-btn-md" onclick={ handleCreateUser }>
        <i class="bi bi-person-plus me-1"></i> Nuevo Usuario
      </button>
    </page-header>

    <loading-spinner
      if={ state.loading }
      message="Cargando usuarios..." />

    <error-alert
      if={ state.error }
      message={ state.errorMessage }
      onretry={ loadUsers } />

    <div if={ !state.loading && !state.error }>
      <div class="card mb-4">
        <div class="card-body">
          <div class="row g-3 align-items-center">
            <div class="col-md-8">
              <label for="userSearch" class="form-label mb-1">Buscar usuarios</label>
              <input
                id="userSearch"
                type="text"
                class="form-control"
                placeholder="Buscar por nombre, email o rol..."
                value={ state.searchTerm }
                oninput={ handleSearch }>
            </div>
            <div class="col-md-4 text-md-end">
              <div class="text-muted small mt-4 mt-md-0">
                { filteredUsers.length } usuario{ filteredUsers.length === 1 ? '' : 's' } encontrado{ filteredUsers.length === 1 ? '' : 's' }
              </div>
            </div>
          </div>
        </div>
      </div>

      <div class="card">
        <div class="table-responsive">
          <table class="table table-hover align-middle mb-0">
            <thead class="table-light">
              <tr>
                <th>Usuario</th>
                <th>Email</th>
                <th>Rol</th>
                <th>Crear dispositivos</th>
                <th>Sentilo</th>
                <th>Innobing</th>
                <th class="text-center">Acciones</th>
              </tr>
            </thead>
            <tbody>
              <tr if={ filteredUsers.length === 0 }>
                <td colspan="7" class="text-center text-muted py-5">
                  <i class="bi bi-people fs-1 d-block mb-2"></i>
                  { state.searchTerm ? 'No hay usuarios que coincidan con la búsqueda' : 'No hay usuarios disponibles' }
                </td>
              </tr>

              <template each={ user in paginatedUsers }>
                <tr>
                  <td>
                    <div class="fw-semibold">{ user.username || 'Sin nombre' }</div>
                    <small class="text-muted">ID: { user._id }</small>
                  </td>
                  <td>{ user.email || 'N/A' }</td>
                  <td>
                    <span class="badge bg-{ getRoleBadgeColor(user) }">{ formatRole(user) }</span>
                  </td>
                  <td>
                    <span class="badge bg-{ user.acl?.createDevice ? 'success' : 'secondary' }">
                      { user.acl?.createDevice ? 'Sí' : 'No' }
                    </span>
                  </td>
                  <td>
                    <span class="badge bg-{ user.sentiloConfig?.enabled ? 'info' : 'secondary' }">
                      { user.sentiloConfig?.enabled ? 'Activo' : 'Desactivado' }
                    </span>
                  </td>
                  <td>
                    <span class="badge bg-{ user.innobingConfig?.enabled ? 'info' : 'secondary' }">
                      { user.innobingConfig?.enabled ? 'Activo' : 'Desactivado' }
                    </span>
                  </td>
                  <td class="text-center table-actions-cell">
                    <div class="btn-group table-action-group" role="group" aria-label="Acciones del usuario">
                      <button
                        type="button"
                        class="btn btn-sm table-action-btn table-action-view"
                        onclick={ () => toggleExpandedUser(user._id) }
                        title="Ver detalles">
                        <i class="bi bi-{ state.expandedUserId === user._id ? 'eye-slash' : 'eye' }"></i>
                      </button>
                      <button
                        type="button"
                        class="btn btn-sm table-action-btn table-action-edit"
                        onclick={ () => handleEditUser(user._id) }
                        title="Editar usuario">
                        <i class="bi bi-pencil"></i>
                      </button>
                      <button
                        type="button"
                        class="btn btn-sm table-action-btn table-action-delete"
                        onclick={ () => handleDeleteUser(user) }
                        title={ isCurrentUser(user) ? 'No puedes eliminar tu propio usuario' : 'Eliminar usuario' }
                        disabled={ state.deletingUserId === user._id || isCurrentUser(user) }>
                        <span if={ state.deletingUserId === user._id } class="spinner-border spinner-border-sm" role="status"></span>
                        <i if={ state.deletingUserId !== user._id } class="bi bi-trash"></i>
                      </button>
                    </div>
                  </td>
                </tr>

                <tr if={ state.expandedUserId === user._id } class="table-light">
                  <td colspan="7">
                    <div class="row g-3 py-2 user-expanded-row">
                      <div class="col-md-6">
                        <div class="small text-muted mb-1">Roles asignados</div>
                        <div>{ formatRolesList(user.roles) }</div>
                      </div>
                      <div class="col-md-6">
                        <div class="small text-muted mb-1">Fechas</div>
                        <div><strong>Creado:</strong> { formatDate(user.createdAt) }</div>
                        <div><strong>Actualizado:</strong> { formatDate(user.updatedAt) }</div>
                      </div>
                    </div>
                  </td>
                </tr>
              </template>
            </tbody>
          </table>
        </div>

        <div if={ totalPages > 1 } class="card-footer d-flex justify-content-between align-items-center flex-wrap gap-2">
          <small class="text-muted">
            Mostrando { startItem } - { endItem } de { filteredUsers.length } usuarios
          </small>

          <nav aria-label="Paginación de usuarios">
            <ul class="pagination pagination-sm mb-0">
              <li class="page-item { state.currentPage === 1 ? 'disabled' : '' }">
                <button class="page-link" type="button" onclick={ () => handlePageChange(state.currentPage - 1) }>
                  Anterior
                </button>
              </li>

              <li each={ page in visiblePages } class="page-item { state.currentPage === page ? 'active' : '' }">
                <button class="page-link" type="button" onclick={ () => handlePageChange(page) }>
                  { page }
                </button>
              </li>

              <li class="page-item { state.currentPage === totalPages ? 'disabled' : '' }">
                <button class="page-link" type="button" onclick={ () => handlePageChange(state.currentPage + 1) }>
                  Siguiente
                </button>
              </li>
            </ul>
          </nav>
        </div>
      </div>
    </div>
  </div>

  <script>
    import PageHeader from './page-header.riot'
    import LoadingSpinner from './loading-spinner.riot'
    import ErrorAlert from './error-alert.riot'
    import { UserService, AuthService } from '../services/api-service'
    import { ToastService } from '../services/toast-service'

    export default {
      components: {
        PageHeader,
        LoadingSpinner,
        ErrorAlert
      },

      onBeforeMount() {
        this.state = {
          loading: true,
          error: false,
          errorMessage: '',
          users: [],
          searchTerm: '',
          expandedUserId: null,
          deletingUserId: null,
          currentUserId: AuthService.getUserId(),
          currentPage: 1,
          pageSize: 10
        }
      },

      async onMounted() {
        if (!AuthService.isAdmin()) {
          this.update({
            loading: false,
            error: true,
            errorMessage: 'Acceso denegado. Debes ser administrador para acceder a la gestión de usuarios.'
          })
          return
        }

        await this.loadUsers()
      },

      get filteredUsers() {
        const users = this.state?.users || []
        const searchTerm = this.state?.searchTerm?.trim().toLowerCase()

        if (!searchTerm) {
          return users
        }

        return users.filter(user => {
          const roleText = this.formatRolesList(user.roles).toLowerCase()
          return (
            user.username?.toLowerCase().includes(searchTerm) ||
            user.email?.toLowerCase().includes(searchTerm) ||
            roleText.includes(searchTerm)
          )
        })
      },

      get totalPages() {
        return Math.max(1, Math.ceil(this.filteredUsers.length / this.state.pageSize))
      },

      get paginatedUsers() {
        const start = (this.state.currentPage - 1) * this.state.pageSize
        const end = start + this.state.pageSize
        return this.filteredUsers.slice(start, end)
      },

      get visiblePages() {
        return Array.from({ length: this.totalPages }, (_, index) => index + 1)
      },

      get startItem() {
        if (this.filteredUsers.length === 0) {
          return 0
        }

        return ((this.state.currentPage - 1) * this.state.pageSize) + 1
      },

      get endItem() {
        return Math.min(this.state.currentPage * this.state.pageSize, this.filteredUsers.length)
      },

      async loadUsers() {
        try {
          this.update({ loading: true, error: false, errorMessage: '' })
          const users = await UserService.getUsers()
          this.update({ loading: false, users: users || [], currentPage: 1 })
        } catch (error) {
          console.error('Error loading users:', error)
          this.update({
            loading: false,
            error: true,
            errorMessage: `Error al cargar los usuarios: ${error.message || 'Error desconocido'}`
          })
          ToastService.error('No se pudo cargar la lista de usuarios')
        }
      },

      handleSearch(e) {
        this.update({ searchTerm: e.target.value, currentPage: 1 })
      },

      handlePageChange(page) {
        if (page < 1 || page > this.totalPages) {
          return
        }

        this.update({ currentPage: page })
      },

      toggleExpandedUser(userId) {
        this.update({
          expandedUserId: this.state.expandedUserId === userId ? null : userId
        })
      },

      handleCreateUser() {
        window.history.pushState({}, '', '/create-user')
        window.dispatchEvent(new PopStateEvent('popstate'))
      },

      handleEditUser(userId) {
        window.history.pushState({}, '', `/create-user?id=${userId}`)
        window.dispatchEvent(new PopStateEvent('popstate'))
      },

      isCurrentUser(user) {
        return Boolean(user && this.state.currentUserId && user._id === this.state.currentUserId)
      },

      async handleDeleteUser(user) {
        if (!user?._id) {
          ToastService.error('No se pudo identificar el usuario a eliminar')
          return
        }

        if (this.isCurrentUser(user)) {
          ToastService.error('No puedes eliminar tu propio usuario desde esta pantalla')
          return
        }

        const confirmed = confirm(`¿Estás seguro de eliminar al usuario "${user.username}"?`)
        if (!confirmed) {
          return
        }

        try {
          this.update({ deletingUserId: user._id })
          await UserService.deleteUser(user._id)

          const remainingUsers = (this.state.users || []).filter(item => item._id !== user._id)
          const maxPage = Math.max(1, Math.ceil(remainingUsers.length / this.state.pageSize))
          this.update({
            users: remainingUsers,
            deletingUserId: null,
            expandedUserId: this.state.expandedUserId === user._id ? null : this.state.expandedUserId,
            currentPage: Math.min(this.state.currentPage, maxPage)
          })

          ToastService.success('Usuario eliminado correctamente')
        } catch (error) {
          console.error('Error deleting user:', error)
          this.update({ deletingUserId: null })
          ToastService.error(`Error al eliminar el usuario: ${error.message || 'Error desconocido'}`)
        }
      },

      handleBack() {
        if (this.props.onback) {
          this.props.onback()
          return
        }

        window.history.pushState({}, '', '/')
        window.dispatchEvent(new PopStateEvent('popstate'))
      },

      formatRole(user) {
        return this.formatRolesList(user.roles)
      },

      formatRolesList(roles) {
        if (!roles || roles.length === 0) {
          return 'Sin rol'
        }

        return roles
          .map(role => role === 'admin' ? 'Administrador' : 'Usuario')
          .join(', ')
      },

      getRoleBadgeColor(user) {
        return user.roles?.includes('admin') ? 'danger' : 'primary'
      },

      formatDate(dateString) {
        if (!dateString) {
          return 'N/A'
        }

        const date = new Date(dateString)
        if (Number.isNaN(date.getTime())) {
          return 'N/A'
        }

        return date.toLocaleString('es-ES')
      }
    }
  </script>

  <style>
    .user-expanded-row {
      font-size: 0.95rem;
    }
  </style>
</user-management>
